import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ORDER_STATUS } from "../constants";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { TooltipWrapper } from "../tooltipWrapper/TooltipWrapper";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import PrintIcon from "@mui/icons-material/Print";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ArchiveIcon from "@mui/icons-material/Archive";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import excelLogo from "../../images/excelLogo.svg";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import showApiErrorNotification from "../showApiErrorNotification";
import { useSnackbar } from "notistack";
import { handlePrintOrderSticker } from "../handlePrint";
import useStatusTranslations from "../../hooks/useStatusTranslations";
import { handleConfirmation } from "../handleConfirmation";
import { formatGeDateTime } from "../dates";
import { useConfirm } from "material-ui-confirm";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const CreateDateBox = ({ className, title, onGoBack }) => {
  return (
    <Box className={className}>
      <Typography variant={"caption"} className={"text-gray-400"}>
        {title}
      </Typography>
      <IconButton onClick={onGoBack} className={"text-gray-700 cursor-pointer smMax:hidden"}>
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

function OrderHeader({ order, archiveOrder, unarchiveOrder, deleteOrder }) {
  const { t } = useTranslation();
  const { trackingCode } = useParams();
  const internationalTrackingCode = order.internationalTrackingNumber;
  const { enqueueSnackbar } = useSnackbar();
  const { formatOrderStatus } = useStatusTranslations();
  const status = formatOrderStatus(order.status);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const creationDate = order.orderDate;
  let creationDateTitle = "";
  if (creationDate) {
    creationDateTitle = `${t("ordersTableHeaders.creationDate")}: ${formatGeDateTime(
      new Date(creationDate),
    )}`;
  }

  const handleCopyOrderNumber = () => {
    navigator.clipboard
      .writeText(trackingCode)
      .then(() => {
        enqueueSnackbar(t("alertsMessages.orderTrackingNumberCopied", { trackingCode }), {
          variant: "success",
        });
      })
      .catch((err) => {
        showApiErrorNotification(
          t("alertsMessages.failedCopyTrackingCode"),
          err,
          enqueueSnackbar,
        );
        console.error("Failed to copy: ", err);
      });
  };

  const handleCopyInternationalNumber = () => {
    navigator.clipboard
      .writeText(internationalTrackingCode)
      .then(() => {
        enqueueSnackbar(
          t("alertsMessages.orderInternationalTrackingNumberCopied", {
            internationalTrackingCode,
          }),
          {
            variant: "success",
          },
        );
      })
      .catch((err) => {
        showApiErrorNotification(
          t("alertsMessages.failedCopyTrackingCode"),
          err,
          enqueueSnackbar,
        );
        console.error(`Failed to copy ITN ${internationalTrackingCode}: `, err);
      });
  };

  const handlePrintOrder = async () => {
    try {
      await handlePrintOrderSticker(trackingCode);
    } catch (error) {
      console.error("Error printing order sticker:", error);
      showApiErrorNotification(t("alertsMessages.failedPrintSticker"), error, enqueueSnackbar);
    }
  };

  const handleCreateSupportTicket = async () => {
    navigate(`/orders/${trackingCode}/support`);
  };

  const handlerArchiveOrders = async () => {
    try {
      const archivedOrders = await archiveOrder();
      if (archivedOrders) {
        enqueueSnackbar(t("alertsMessages.orderArchived"), {
          variant: "success",
        });
      } else {
        enqueueSnackbar(t("alertsMessages.failedArchiveOrder"), {
          variant: "error",
        });
        console.error(`Error archive order: ${trackingCode}`);
      }
    } catch (error) {
      showApiErrorNotification("alertsMessages.failedArchiveOrder", error, enqueueSnackbar);
      console.error(`Error archive order: ${trackingCode}`, error);
    }
  };

  const handlerUnarchiveOrders = async () => {
    try {
      const unarchivedOrders = await unarchiveOrder();
      if (unarchivedOrders) {
        enqueueSnackbar(t("alertsMessages.orderUnarchived"), {
          variant: "success",
        });
      } else {
        enqueueSnackbar(t("alertsMessages.failedUnarchiveOrder"), {
          variant: "error",
        });
        console.error(`Error unarchive order: ${trackingCode}`);
      }
    } catch (error) {
      showApiErrorNotification(t("alertsMessages.failedUnarchiveOrder"), error, enqueueSnackbar);
      console.error(`Error unarchive order: ${trackingCode}`, error);
    }
  };

  const handleGoBack = useCallback(() => {
    navigate("/orders/new");
  }, [navigate]);

  const handleDeleteOrder = useCallback(async () => {
    try {
      const orderDeleted = deleteOrder();
      if (orderDeleted) {
        enqueueSnackbar(t("alertsMessages.orderDeleted"), {
          variant: "success",
        });
        handleGoBack();
        console.info(`Order deleted ${trackingCode}`);
      } else {
        enqueueSnackbar(t("alertsMessages.failedOrderDelete"), {
          variant: "error",
        });
        console.error(`Failed to delete the order ${trackingCode}`);
      }
    } catch (e) {
      showApiErrorNotification(t("alertsMessages.failedOrderDelete"), e, enqueueSnackbar);
      console.error(`Failed to delete the order: ${trackingCode}`, e);
    }
  }, [enqueueSnackbar, t, handleGoBack, trackingCode, deleteOrder]);

  const handleDeleteConfirmation = useCallback(
    async (id) => {
      const confirmationDetails = {
        description: t("confirmation.confirmDeleteOrder", { id }),
      };
      await handleConfirmation(confirm, confirmationDetails, () => handleDeleteOrder(id), t);
    },
    [confirm, handleDeleteOrder, t],
  );

  return (
    <Box className={"smMax:px-3 md:pt-4 md:pb-6 md:px-3"}>
      {/* Wide screen creation date */}
      <CreateDateBox
        title={creationDateTitle}
        onGoBack={handleGoBack}
        className={"flex items-center justify-between ml-3 smMax:hidden"}
      />
      <Box className={"flex md:items-end md:justify-between smMax:flex-col"}>
        <Box className={"flex smMax:flex-col md:flex-row md:items-center flex-wrap"}>
          <Box className={"flex items-center cursor-pointer"} onClick={handleCopyOrderNumber}>
            <TooltipWrapper title={t("tooltips.copyTrackingCode")}>
              <IconButton>
                <DescriptionOutlinedIcon
                  className={"smMax:w-6 smMax:h-6 w-9 h-9 text-gray-700"}
                />
              </IconButton>
            </TooltipWrapper>
            <Typography className={"text-xl md:text-3xl"}>{trackingCode}</Typography>
          </Box>
          {Boolean(internationalTrackingCode) && (
            <Box
              className={"flex items-center cursor-pointer smMax:w-full md:mr-3"}
              onClick={handleCopyInternationalNumber}
            >
              <TooltipWrapper title={t("tooltips.copyInternationalTrackingCode")}>
                <IconButton>
                  <DescriptionIcon className={"smMax:w-6 smMax:h-6 w-9 h-9 text-gray-700"} />
                </IconButton>
              </TooltipWrapper>
              <Box className={"flex flex-col"}>
                <Typography>{internationalTrackingCode}</Typography>
                <Typography variant="caption" className={"text-gray-500"}>
                  {t("orderTracking.internationalTrackingCode")}
                </Typography>
              </Box>
            </Box>
          )}
          {/* Small screen creation date */}
          <CreateDateBox
            title={creationDateTitle}
            onGoBack={handleGoBack}
            className={"flex items-center justify-between ml-3 my-1 md:px-4 md:hidden"}
          />
          <Box className={"flex gap-1 smMax:ml-2 md:mt-1 md:ml-2"}>
            {Boolean(order.returnParcelToSender) && (
              <Chip label={t("orderTracking.withReturn")} className={""} />
            )}
            {Boolean(status) && <Chip label={status} className={""} color={"primary"} />}
            {Boolean(order.isArchived) && (
              <Chip label={t("tooltips.archivedFlag")} className={""} />
            )}
          </Box>
        </Box>
        <Box className={"flex items-center justify-end text-gray-500"}>
          <TooltipWrapper title={t("tooltips.createSupportTicket")}>
            <IconButton onClick={handleCreateSupportTicket}>
              <SupportAgentIcon className={"m-1 cursor-pointer"} />
            </IconButton>
          </TooltipWrapper>
          <TooltipWrapper title={t("tooltips.printSticker")}>
            <IconButton onClick={handlePrintOrder}>
              <PrintIcon className={"m-1 cursor-pointer"} />
            </IconButton>
          </TooltipWrapper>
          {order.isArchived ? (
            <TooltipWrapper title={t("tooltips.unarchiveOrder")}>
              <IconButton
                onClick={() => {
                  handlerUnarchiveOrders([trackingCode]);
                }}
              >
                <UnarchiveIcon className={"m-1 cursor-pointer"} />
              </IconButton>
            </TooltipWrapper>
          ) : (
            <TooltipWrapper title={t("tooltips.archiveOrder")}>
              <IconButton
                onClick={() => {
                  handlerArchiveOrders([trackingCode]);
                }}
              >
                <ArchiveIcon className={"m-1 cursor-pointer"} />
              </IconButton>
            </TooltipWrapper>
          )}
          <TooltipWrapper title={t("tooltips.addToRegister")}>
            <IconButton className={"hidden"}>
              <FolderSpecialIcon className={"m-1 cursor-pointer"} />
            </IconButton>
          </TooltipWrapper>
          <TooltipWrapper title={t("tooltips.addToExcel")}>
            <IconButton className={"hidden"}>
              <img alt={"excel"} src={excelLogo} className={"m-1 cursor-pointer"} />
            </IconButton>
          </TooltipWrapper>
          {order.status === ORDER_STATUS.DRAFT && (
            <>
              <TooltipWrapper title={t("tooltips.addToArchive")}>
                <IconButton className={"hidden"}>
                  <ArchiveIcon className={"m-1 cursor-pointer"} />
                </IconButton>
              </TooltipWrapper>
              <TooltipWrapper title={t("tooltips.editOrder")}>
                <IconButton
                  onClick={() => {
                    navigate(`/orders/${trackingCode}/edit`);
                  }}
                >
                  <EditIcon className={"m-1"} />
                </IconButton>
              </TooltipWrapper>
              <TooltipWrapper title={t("tooltips.deleteOrder")}>
                <IconButton onClick={() => handleDeleteConfirmation(trackingCode)}>
                  <DeleteIcon className={"m-1"} />
                </IconButton>
              </TooltipWrapper>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default OrderHeader;
